/* RESPONSIBLE TEAM: team-reporting */
/* eslint-disable @intercom/intercom/no-bare-strings */
import type IntlService from 'ember-intl/services/intl';
import { type Filter, type LogicalFilter } from 'embercom/components/reporting/custom/filters';
// @ts-ignore no type declaration available for ember-copy
import { copy } from 'ember-copy';

export function and(...filters: any): LogicalFilter {
  filters = filters.compact();
  if (filters.length === 1) {
    return filters[0];
  }

  return {
    type: 'and',
    filters: filters.flatMap((f: any) => (f.type === 'and' ? f.filters : f)),
  };
}

export function or(...filters: Filter[]): Filter | LogicalFilter {
  let properties = filters.map((f: Filter) => f.data.property).uniq();
  let allAreCategoryFilters = filters.every((f: Filter) => f.type === 'category');
  if (properties.length === 1 && allAreCategoryFilters) {
    // merge the filters into a single filter
    return {
      type: 'category',
      data: {
        property: filters[0].data.property,
        attribute: filters[0].data.attribute,
        values: filters.flatMap((f: Filter) => f.data.values),
      },
    };
  } else {
    return {
      type: 'or',
      filters: filters.flatMap((f: any) => (f.type === 'or' ? f.filters : f)),
    };
  }
}

export function not(filter: any): Filter {
  filter = copy(filter, true);
  if (filter.type === 'category') {
    // The "Fin AI Agent involved" data attribute uses a filter type that doesn't
    // support "not_in_category", so if it's a Boolean category, we can just negate
    let values = filter.data.values;
    if (values.length === 1 && typeof values.firstObject === 'boolean') {
      filter.data.values = [!values.firstObject];
    } else {
      filter.type = 'not_in_category';
    }
  } else if (filter.type === 'exists') {
    filter.type = 'not_exists';
  } else {
    throw new Error(`Unsupported filter type: ${filter.type}`);
  }
  return filter;
}

const filters = {
  get chatConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Desktop'],
      },
    };
  },
  get emailConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Email'],
      },
    };
  },
  get phoneConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Phone call'],
      },
    };
  },
  get androidConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Android App'],
      },
    };
  },
  get iosConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['iOS App'],
      },
    };
  },
  get twitterConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Twitter'],
      },
    };
  },
  get facebookMessengerConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Facebook Messenger'],
      },
    };
  },
  get facebookConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Facebook'],
      },
    };
  },
  get whatsappConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['WhatsApp'],
      },
    };
  },
  get instagramConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Instagram'],
      },
    };
  },
  get phoneSwitchConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Phone switch'],
      },
    };
  },
  get smsConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['SMS'],
      },
    };
  },
  get zendeskTicketConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Zendesk ticket'],
      },
    };
  },
  get salesforceCaseConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Salesforce case'],
      },
    };
  },
  get zendeskMessagingConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Zendesk Messaging'],
      },
    };
  },
  get slackConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Slack'],
      },
    };
  },
  get unknownConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Unknown'],
      },
    };
  },
  // Main filter for resolved conversations - use this instead of individual resolution state filters
  get finResolved() {
    return {
      type: 'category',
      data: {
        property: 'fin.resolution_state',
        attribute: 'conversation.fin.resolution_state',
        values: ['confirmed_resolved', 'assumed_resolved'],
      },
    };
  },

  get finRoutedToTeam() {
    return {
      type: 'category',
      data: {
        property: 'conversation_custom_fields#ai_chatbot_resolution_state',
        attribute: 'conversation_custom_fields#ai_chatbot_resolution_state',
        values: ['routed_to_team'],
      },
    };
  },

  get finResolvedOrRoutedToTeam() {
    return {
      type: 'category',
      data: {
        property: 'conversation_custom_fields#ai_chatbot_resolution_state',
        attribute: 'conversation_custom_fields#ai_chatbot_resolution_state',
        values: ['hard_resolution', 'soft_resolution', 'routed_to_team', 'procedure_handoff'],
      },
    };
  },
  get finPending() {
    return not(this.finResolvedOrRoutedToTeam);
  },

  get finNotResolved() {
    return not(this.finResolved);
  },

  get finInvolved() {
    return {
      type: 'category',
      data: {
        property: 'fin.participated',
        attribute: 'conversation.fin.participated',
        values: [true],
      },
    };
  },
  get finNotInvolved() {
    return not(this.finInvolved);
  },

  get finOnlyResolved() {
    return and(this.finResolved, not(this.teammateWasEverInvolved));
  },

  get positiveCSAT() {
    return {
      type: 'category',
      data: {
        property: 'ai_generated_metrics.csat',
        attribute: 'conversation.ai_generated_metrics.csat',
        values: ['4', '5'],
      },
    };
  },
  get neutralCSAT() {
    return {
      type: 'category',
      data: {
        property: 'ai_generated_metrics.csat',
        attribute: 'conversation.ai_generated_metrics.csat',
        values: ['3'],
      },
    };
  },
  get negativeCSAT() {
    return {
      type: 'category',
      data: {
        property: 'ai_generated_metrics.csat',
        attribute: 'conversation.ai_generated_metrics.csat',
        values: ['1', '2'],
      },
    };
  },

  get finAndTeammate() {
    return and(
      this.finInvolved,
      this.teammateWasEverInvolved,
      not(this.finResolved),
      not(this.finProcedureHandoff),
    );
  },

  get finAndTeammateNew() {
    return and(this.finInvolved, this.finRoutedToTeam);
  },

  get teammateWasEverInvolved() {
    return {
      type: 'exists',
      data: {
        property: 'admin_participant_ids',
        attribute: 'conversation.admin_participant_ids',
      },
    };
  },
  get teammateWasNeverInvolved() {
    return not(this.teammateWasEverInvolved);
  },

  get finProcedureHandoff() {
    return {
      type: 'category',
      data: {
        property: 'fin.resolution_state',
        attribute: 'conversation.fin.resolution_state',
        values: ['procedure_handoff'],
      },
    };
  },

  get finEscalationRulesOrGuidanceApplied() {
    return {
      type: 'category',
      data: {
        property: 'conversation_custom_fields#configuration_based_escalation_reason',
        attribute: 'conversation_custom_fields#configuration_based_escalation_reason',
        values: ['guidance_applied', 'escalation_rule_applied'],
      },
    };
  },

  get finEscalationUserRequested() {
    return {
      type: 'category',
      data: {
        property: 'conversation_custom_fields#fin_escalation_user_requested',
        attribute: 'conversation_custom_fields#fin_escalation_user_requested',
        values: [true],
      },
    };
  },
};

export function wrap(filters: any) {
  // because of assumptions made elsewhere in the code, it's easier to
  // just make sure there's always a top level logical filter
  if (filters && filters.type !== 'and' && filters.type !== 'or') {
    return {
      type: 'and',
      filters: [filters],
    };
  }
  return filters;
}

export function funnelSeries(filters?: any, label?: string) {
  return {
    label,
    metric_id: 'v1.new_conversations',
    filters: wrap(filters),
  };
}

function config(name: string, filters?: any) {
  return { name, series: funnelSeries(filters, name) };
}

interface Metrics {
  conversations: number;

  Chat: number;
  Email: number;
  Phone: number;
  Android: number;
  iOS: number;
  Twitter: number;
  FacebookMessenger: number;
  Facebook: number;
  WhatsApp: number;
  Instagram: number;
  PhoneSwitch: number;
  SMS: number;
  ZendeskTicket: number;
  SalesforceCase: number;
  ZendeskMessaging: number;
  Slack: number;
  Unknown: number;

  ChatAndFinInvolved: number;
  EmailAndFinInvolved: number;
  ChatAndFinNotInvolved: number;
  EmailAndFinNotInvolved: number;
  AndroidAndFinInvolved: number;
  AndroidAndFinNotInvolved: number;
  iOSAndFinInvolved: number;
  iOSAndFinNotInvolved: number;
  TwitterAndFinInvolved: number;
  TwitterAndFinNotInvolved: number;
  PhoneAndFinInvolved: number;
  PhoneAndFinNotInvolved: number;
  FacebookMessengerAndFinInvolved: number;
  FacebookMessengerAndFinNotInvolved: number;
  FacebookAndFinInvolved: number;
  FacebookAndFinNotInvolved: number;
  WhatsAppAndFinInvolved: number;
  WhatsAppAndFinNotInvolved: number;
  InstagramAndFinInvolved: number;
  InstagramAndFinNotInvolved: number;
  PhoneSwitchAndFinInvolved: number;
  PhoneSwitchAndFinNotInvolved: number;
  SMSAndFinInvolved: number;
  SMSAndFinNotInvolved: number;
  ZendeskTicketAndFinInvolved: number;
  ZendeskTicketAndFinNotInvolved: number;
  SalesforceCaseAndFinInvolved: number;
  SalesforceCaseAndFinNotInvolved: number;
  ZendeskMessagingAndFinInvolved: number;
  ZendeskMessagingAndFinNotInvolved: number;
  SlackAndFinInvolved: number;
  SlackAndFinNotInvolved: number;
  UnknownAndFinInvolved: number;
  UnknownAndFinNotInvolved: number;

  FinInvolved: number;
  FinNotInvolved: number;

  FinResolved: number;
  FinResolvedAndPositiveCSAT: number;
  FinResolvedAndNegativeCSAT: number;
  FinResolvedAndNeutralCSAT: number;

  FinAbandoned: number;
  FinAndTeammate: number;
  FinEscalationRulesAndGuidance: number;
  FinEscalationUserRequested: number;
  FinProcedureHandoff: number;
}

// IMPORTANT: When adding metrics downstream of FinInvolved (resolution outcomes, CSAT, etc.),
// always intersect with f.finInvolved — e.g. and(f.finInvolved, f.myNewFilter).
// Role-scoped configs override f.finInvolved to use a role-specific participation signal
// (e.g. fin_service.participated). Without the intersection, the metric will count
// conversations from ALL Fin roles, not just the scoped one.
function buildChartSeriesFilters(f: typeof filters) {
  let finAbandonedFilter = f.finPending;
  let finAndTeammateFilter = f.finAndTeammateNew;

  return [
    config('conversations'),

    config('Chat', f.chatConversations),
    config('Email', f.emailConversations),
    config('Phone', f.phoneConversations),
    config('Android', f.androidConversations),
    config('iOS', f.iosConversations),
    config('Twitter', f.twitterConversations),
    config('FacebookMessenger', f.facebookMessengerConversations),
    config('Facebook', f.facebookConversations),
    config('WhatsApp', f.whatsappConversations),
    config('Instagram', f.instagramConversations),
    config('PhoneSwitch', f.phoneSwitchConversations),
    config('SMS', f.smsConversations),
    config('ZendeskTicket', f.zendeskTicketConversations),
    config('SalesforceCase', f.salesforceCaseConversations),
    config('ZendeskMessaging', f.zendeskMessagingConversations),
    config('Slack', f.slackConversations),
    config('Unknown', f.unknownConversations),

    config('ChatAndFinInvolved', and(f.chatConversations, f.finInvolved)),
    config('EmailAndFinInvolved', and(f.emailConversations, f.finInvolved)),
    config('PhoneAndFinInvolved', and(f.phoneConversations, f.finInvolved)),
    config('AndroidAndFinInvolved', and(f.androidConversations, f.finInvolved)),
    config('iOSAndFinInvolved', and(f.iosConversations, f.finInvolved)),
    config('TwitterAndFinInvolved', and(f.twitterConversations, f.finInvolved)),
    config('ChatAndFinNotInvolved', and(f.chatConversations, f.finNotInvolved)),
    config('EmailAndFinNotInvolved', and(f.emailConversations, f.finNotInvolved)),
    config('PhoneAndFinNotInvolved', and(f.phoneConversations, f.finNotInvolved)),
    config('AndroidAndFinNotInvolved', and(f.androidConversations, f.finNotInvolved)),
    config('iOSAndFinNotInvolved', and(f.iosConversations, f.finNotInvolved)),
    config('TwitterAndFinNotInvolved', and(f.twitterConversations, f.finNotInvolved)),
    config('FacebookMessengerAndFinInvolved', and(f.facebookMessengerConversations, f.finInvolved)),
    config(
      'FacebookMessengerAndFinNotInvolved',
      and(f.facebookMessengerConversations, f.finNotInvolved),
    ),
    config('FacebookAndFinInvolved', and(f.facebookConversations, f.finInvolved)),
    config('FacebookAndFinNotInvolved', and(f.facebookConversations, f.finNotInvolved)),
    config('WhatsAppAndFinInvolved', and(f.whatsappConversations, f.finInvolved)),
    config('WhatsAppAndFinNotInvolved', and(f.whatsappConversations, f.finNotInvolved)),
    config('InstagramAndFinInvolved', and(f.instagramConversations, f.finInvolved)),
    config('InstagramAndFinNotInvolved', and(f.instagramConversations, f.finNotInvolved)),
    config('PhoneSwitchAndFinInvolved', and(f.phoneSwitchConversations, f.finInvolved)),
    config('PhoneSwitchAndFinNotInvolved', and(f.phoneSwitchConversations, f.finNotInvolved)),
    config('SMSAndFinInvolved', and(f.smsConversations, f.finInvolved)),
    config('SMSAndFinNotInvolved', and(f.smsConversations, f.finNotInvolved)),
    config('ZendeskTicketAndFinInvolved', and(f.zendeskTicketConversations, f.finInvolved)),
    config('ZendeskTicketAndFinNotInvolved', and(f.zendeskTicketConversations, f.finNotInvolved)),
    config('SalesforceCaseAndFinInvolved', and(f.salesforceCaseConversations, f.finInvolved)),
    config('SalesforceCaseAndFinNotInvolved', and(f.salesforceCaseConversations, f.finNotInvolved)),
    config('ZendeskMessagingAndFinInvolved', and(f.zendeskMessagingConversations, f.finInvolved)),
    config(
      'ZendeskMessagingAndFinNotInvolved',
      and(f.zendeskMessagingConversations, f.finNotInvolved),
    ),
    config('SlackAndFinInvolved', and(f.slackConversations, f.finInvolved)),
    config('SlackAndFinNotInvolved', and(f.slackConversations, f.finNotInvolved)),
    config('UnknownAndFinInvolved', and(f.unknownConversations, f.finInvolved)),
    config('UnknownAndFinNotInvolved', and(f.unknownConversations, f.finNotInvolved)),

    config('FinInvolved', f.finInvolved),
    config('FinNotInvolved', f.finNotInvolved),

    // resolution flows — scoped to finInvolved so role-scoped configs
    // (e.g. fin_service.participated) don't leak resolutions from other roles
    config('FinResolved', and(f.finInvolved, f.finResolved)),
    config('FinResolvedAndPositiveCSAT', and(f.finInvolved, f.finResolved, f.positiveCSAT)),
    config('FinResolvedAndNeutralCSAT', and(f.finInvolved, f.finResolved, f.neutralCSAT)),
    config('FinResolvedAndNegativeCSAT', and(f.finInvolved, f.finResolved, f.negativeCSAT)),

    config('FinAbandoned', and(f.finInvolved, finAbandonedFilter)),
    config('FinAndTeammate', finAndTeammateFilter),
    config(
      'FinEscalationRulesAndGuidance',
      and(f.finInvolved, f.finRoutedToTeam, f.finEscalationRulesOrGuidanceApplied),
    ),
    config(
      'FinEscalationUserRequested',
      and(f.finInvolved, f.finRoutedToTeam, f.finEscalationUserRequested),
    ),
    config('FinProcedureHandoff', and(f.finInvolved, f.finProcedureHandoff)),
  ];
}

function buildFunnelConfig(filtersObj: typeof filters) {
  return {
    get filters() {
      return filtersObj;
    },

    chartSeriesFilters() {
      return buildChartSeriesFilters(this.filters);
    },

    chartSeries() {
      return this.chartSeriesFilters().map((c) => c.series);
    },

    getChartSeriesWithLabels(intl: IntlService) {
      return this.chartSeriesFilters().map((c) => ({
        ...c.series,
        label: intl.t(`ai-agent.analyze.performance.funnel-metrics.${c.name}`),
      }));
    },

    getMetricsFromChartDataRequest(chartData: any) {
      let result = {} as Metrics;
      let seriesFilters = this.chartSeriesFilters();
      for (let i = 0; i < chartData.length; i++) {
        let seriesName = seriesFilters[i].name as keyof Metrics;
        let seriesData = chartData[i].data[0];
        result[seriesName] = seriesData;
      }
      return result;
    },
  };
}

/**
 * Creates a funnel config scoped to a role-specific participation signal.
 * The participation property (e.g. 'fin_service.participated' or 'fin_ecommerce.participated')
 * is used to override finInvolved/finNotInvolved filters, while all other shared
 * fin.* filters (resolution, escalation, etc.) remain unchanged.
 *
 * When useBackwardCompat is true, uses OR filters to match the backend metric
 * definitions (see ReportingService::MetricService::Metrics::Fin::Service).
 * Older conversation docs may only have the shared fin.participated field set,
 * without the role-specific field. The OR filter catches both:
 *   - New docs: role-specific field exists and is true/false
 *   - Old docs: shared fin.participated is true/false AND role-specific field doesn't exist
 *
 * Only fin_service needs backward compat — before ecommerce existed, all Fin
 * conversations were service. Ecommerce is new so all its docs have the field.
 */
export function createRoleScopedFunnelConfig(
  participatedProperty: string,
  { useBackwardCompat = false }: { useBackwardCompat?: boolean } = {},
) {
  let roleScopedFilters = Object.create(filters);

  if (useBackwardCompat) {
    Object.defineProperty(roleScopedFilters, 'finInvolved', {
      get() {
        return {
          type: 'or',
          filters: [
            {
              type: 'category',
              data: {
                property: participatedProperty,
                attribute: `conversation.${participatedProperty}`,
                values: [true],
              },
            },
            {
              type: 'and',
              filters: [
                {
                  type: 'category',
                  data: {
                    property: 'fin.participated',
                    attribute: 'conversation.fin.participated',
                    values: [true],
                  },
                },
                {
                  type: 'not_exists',
                  data: {
                    property: participatedProperty,
                    attribute: `conversation.${participatedProperty}`,
                  },
                },
              ],
            },
          ],
        };
      },
    });
    Object.defineProperty(roleScopedFilters, 'finNotInvolved', {
      get() {
        return {
          type: 'or',
          filters: [
            {
              type: 'category',
              data: {
                property: participatedProperty,
                attribute: `conversation.${participatedProperty}`,
                values: [false],
              },
            },
            {
              type: 'and',
              filters: [
                {
                  type: 'category',
                  data: {
                    property: 'fin.participated',
                    attribute: 'conversation.fin.participated',
                    values: [false],
                  },
                },
                {
                  type: 'not_exists',
                  data: {
                    property: participatedProperty,
                    attribute: `conversation.${participatedProperty}`,
                  },
                },
              ],
            },
          ],
        };
      },
    });
  } else {
    Object.defineProperty(roleScopedFilters, 'finInvolved', {
      get() {
        return {
          type: 'category',
          data: {
            property: participatedProperty,
            attribute: `conversation.${participatedProperty}`,
            values: [true],
          },
        };
      },
    });
    Object.defineProperty(roleScopedFilters, 'finNotInvolved', {
      get() {
        return not(this.finInvolved);
      },
    });
  }

  return buildFunnelConfig(roleScopedFilters);
}

export const FinPerformanceFunnelConfig = buildFunnelConfig(filters);
